import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FocusOn } from 'react-focus-on';
import type { TransitionStatus } from 'react-transition-group';
import { Transition } from 'react-transition-group';

import { createElementWithId } from '@farmersdog/utils';
import classNames from 'classnames';

import styles from './Lightbox.module.css';

import { Animation } from '../../styles';

const portalRootId = `tfd-lightbox`;

export interface LightboxProps {
  /**
   * Whether the Lightbox should be open when rendered.
   *
   * @defaultValue false
   */
  isOpen?: boolean;

  /** A CSS class to apply to the content container. */
  contentClassName?: string;
  /** A style to apply to the content container. */
  contentStyle?: Omit<CSSProperties, 'width' | 'minHeight'>;

  /** A CSS class to add to the background behind the lightbox. */
  backdropClassName?: string;
  /** The style of the background behind the lightbox. */
  backdropStyle?: CSSProperties;
  /** A CSS class for the root element. */
  className?: string;
  /**
   * Whether the lightbox should be closed when the clicking outside it. TODO:
   * rename to `closeOnClickOutside` to reflect the focus-on options
   *
   * @defaultValue true
   */
  closeOnBackdropClick?: boolean;
  /**
   * ARIA label for the backdrop.
   *
   * @defaultValue Close
   */
  backdropLabel?: string;
  /**
   * Animate the transition between open/closed status.
   *
   * @defaultValue true
   */
  animate?: boolean;
  /**
   * The minimum height of the lightbox.
   *
   * @defaultValue 100dvh
   */
  height?: string | number;
  /**
   * The width of the Lightbox.
   *
   * @defaultValue 100%
   */
  width?: string | number;
  /**
   * Distance from the top.
   *
   * @defaultValue 0
   */
  offsetTop?: number;
  /**
   * An array of containers (refs created with with `useRef`) to use as focus
   * trap.
   *
   * As default, the focus is trapped inside the dialog's content, but you can
   * specify more elements here.
   */
  focusTraps?: RefObject<HTMLElement>[];

  /** The content of the lightbox. */
  children?: ReactNode;
  /** Callback when the lightbox is being open (when the transition starts). */
  onOpen?: () => void;
  /** Callback when the lightbox has been opened (when the transition starts). */
  onOpened?: () => void;
  /** Callback when the lightbox is being closed (when the transition starts). */
  onClose?: () => void;
  /** Callback when the lightbox has been closed (after the transition ends). */
  onClosed?: () => void;
  /**
   * Callback when the lightbox has a close action taken, like using the Esc key
   * or clicking a backdrop. The controlling component can then manage the state
   * external to this component.
   */
  onCloseRequested?: () => void;
  /**
   * Override the role in case you are using the Lightbox to display popover
   * menus etc.
   *
   * @defaultValue dialog
   */
  role?: string;
  /**
   * Control the visibility of the lightbox. When `true`, the lightbox will be
   * hidden but not unmounted from the DOM.
   */
  controlVisibility?: boolean;

  ['aria-label']?: string;
  ['aria-labelledby']?: string;
  ['aria-description']?: string;
  ['aria-describedby']?: string;
  ['data-testid']?: string;
}

/**
 * Render a lightbox dialog with a backdrop.
 *
 * @cssVariable --tfd-lightbox-z-index
 * @see https://corgi-x.tfd.engineering/components/lightbox
 */
export function Lightbox(props: LightboxProps) {
  const {
    animate = true,
    className,
    closeOnBackdropClick = false,
    offsetTop = 0,
    width = '100%',
    height = '100dvh',
    role = 'dialog',
    controlVisibility = false,
    onCloseRequested,
  } = props;

  const transitionDuration = animate ? Animation.StandardDuration : 0;
  const dialogRef = useRef<HTMLDivElement>(null);

  const [isLightboxOpen, setIsLightboxOpen] = useState(props.isOpen ?? false);

  // Toggle the lightbox when the `isOpen` prop changes.
  useEffect(() => {
    if (isLightboxOpen !== props.isOpen) {
      setIsLightboxOpen(props.isOpen ?? false);
    }
  }, [isLightboxOpen, props.isOpen]);

  const closeLightbox = () => setIsLightboxOpen(false);

  const dialogClassName = classNames(styles.dialog, className, {
    [styles.disableAnimation]: !animate,
  });

  const contentStyle: CSSProperties = {
    width,
    minHeight: `calc(${height} - ${offsetTop}px)`,
    ...props.contentStyle,
  };

  // add a useState hook to avoid SSR hydration errors since this won't run
  // on the server or client until after the useEffect happens
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return mounted
    ? createPortal(
        <Transition
          onEnter={props.onOpen}
          onEntered={props.onOpened}
          onExit={props.onClose}
          onExited={props.onClosed}
          in={isLightboxOpen}
          component={null}
          timeout={transitionDuration}
          mountOnEnter={!controlVisibility}
          unmountOnExit={!controlVisibility}
          appear
          nodeRef={dialogRef}
        >
          {(transitionStatus: TransitionStatus) => {
            const contentClassName = classNames(
              styles.content,
              props.contentClassName,
              styles[transitionStatus]
            );
            const backdropClassName = classNames(
              styles.backdrop,
              props.backdropClassName,
              styles[transitionStatus]
            );
            const dialogStyle: CSSProperties = {
              marginTop: offsetTop,
              visibility:
                controlVisibility && transitionStatus === 'exited'
                  ? 'hidden'
                  : undefined,
            };
            return (
              <FocusOn
                enabled={isLightboxOpen}
                autoFocus
                focusLock
                scrollLock
                shards={props.focusTraps}
                onClickOutside={
                  closeOnBackdropClick ? closeLightbox : undefined
                }
                onEscapeKey={onCloseRequested ?? closeLightbox}
              >
                <div
                  ref={dialogRef}
                  className={dialogClassName}
                  style={dialogStyle}
                  role={role}
                  aria-modal={role === 'dialog' ? true : undefined}
                  aria-label={props['aria-label']}
                  aria-labelledby={props['aria-labelledby']}
                  aria-description={props['aria-description']}
                  aria-describedby={props['aria-describedby']}
                  data-testid={props['data-testid']}
                >
                  <div
                    className={backdropClassName}
                    style={props.backdropStyle}
                    onClick={
                      closeOnBackdropClick
                        ? (onCloseRequested ?? closeLightbox)
                        : undefined
                    }
                  />
                  <div className={styles.container}>
                    <div className={contentClassName} style={contentStyle}>
                      {props.children}
                    </div>
                  </div>
                </div>
              </FocusOn>
            );
          }}
        </Transition>,
        createElementWithId(portalRootId, 'div')
      )
    : null;
}
